import React, {
  useImperativeHandle,
  forwardRef,
  useState,
  useEffect,
} from "react";
import { Autocomplete, Grid, Skeleton, TextField } from "@mui/material";
import {
  listAllEmployees,
  listPayHeadApi,
  VandRAmtForEmployee,
} from "../../parollAPIs";
import { categoryOptions } from "../../payrollEnums";
import { useSelector } from "react-redux";

const VariableAndRecurringLayout = forwardRef(
  ({ section, loading, singleViewId, isEdit, category }, ref) => {
    const loginUserData = JSON.parse(localStorage.getItem("login_meta"));
    const { singleViewData } = useSelector((state) => state.payrollSlice);
    const [payHead, setPayHead] = useState([]);
    const [employeeList, setEmployeeList] = useState([]);
    const [formData, setFormData] = useState({
      payHeadAllowance: null,
      employee: null,
      date: new Date().toISOString().split("T")[0],
      from: new Date().toISOString().split("T")[0],
      to: new Date().toISOString().split("T")[0],
      count: "",
      amount: null,
    });

    useEffect(() => {
      if (!singleViewId) {
        if (formData.from && formData.to) {
          const fromDate = new Date(formData.from);
          const toDate = new Date(formData.to);
          // Calculate the day difference
          const dayDifference = Math.floor(
            (toDate - fromDate) / (1000 * 60 * 60 * 24)
          );
          // Update `count` in formData
          setFormData((prevData) => ({
            ...prevData,
            count: dayDifference,
          }));
        }
      }
    }, [formData.from, formData.to]);

    const [errors, setErrors] = useState({});

    const handleChange = (prop) => (e, value) => {
      let newValue = e?.target?.value || value;

      if (prop === "count" || prop === "amount") {
        newValue = Number(e?.target?.value) || "";
      }

      setFormData((prev) => ({
        ...prev,
        [prop]: newValue,
      }));

      setErrors((prev) => ({  
        ...prev,
        [prop]: "",
      }));
    };

    const validateForm = () => {
      const requiredFields = {
        common: {
          payHeadAllowance: "Pay Head Allowance is required",
          employee: "Employee is required",
          amount: "Amount is required",
          from: "From date is required",
        },
        Variable: {
          to: "To date is required",
          // count: "Count is required",
        },
        // Recurring: {
        //   date: "Date is required",
        // },
      };

      const fieldsToValidate = {
        ...requiredFields.common,
        ...(requiredFields[section] || {}),
      };

      const validationErrors = Object.entries(fieldsToValidate).reduce(
        (errors, [field, errorMessage]) => {
          if (!formData[field]) errors[field] = errorMessage;
          return errors;
        },
        {}
      );

      setErrors(validationErrors);
      return Object.keys(validationErrors).length === 0;
    };

    useImperativeHandle(ref, () => ({
      getValidatedFormData: () => {
        if (validateForm()) {
          return formData;
        }
        return null;
      },
    }));

    useEffect(() => {
      listPayHeadApi({
        body: {
          page: 1,
          limit: 0,
          search: "",
          category:
            category === "Allowance"
              ? [
                  categoryOptions.ALLOWANCE,
                  categoryOptions.BONUS,
                  categoryOptions.COMMISSION,
                  categoryOptions.BENEFITS,
                ]
              : category === "Deduction"
              ? [
                  categoryOptions.REIMBURSEMENTS,
                  categoryOptions.LEAVE_ENCASHMENT,
                  categoryOptions.DEDUCTION,
                ]
              : "",
        },
        setPayHead,
      });

      listAllEmployees({
        branchId: loginUserData?.branchPk || "",
        setEmployeeList,
      });
    }, []);

    useEffect(() => {
      if (singleViewData && payHead.length && employeeList.length) {
        const selectedPayHead = payHead.find(
          (item) => item._id === singleViewData.payHeadId
        );
        const selectedEmployee = employeeList.find(
          (item) => item._id === singleViewData.empId
        );

        const formattedFormData = {
          payHeadAllowance: selectedPayHead || null,
          employee: selectedEmployee || null,
          date:
            singleViewData?.date?.split(" ")[0] ||
            new Date().toISOString().split("T")[0],
          from:
            singleViewData?.date?.split(" ")[0] ||
            new Date().toISOString().split("T")[0],
          to:
            singleViewData?.dateTo?.split(" ")[0] ||
            new Date().toISOString().split("T")[0],
          count: singleViewData.count || "",
          amount: singleViewData.amount || "",
          isRecurring: singleViewData.isRecurring,
          isAllowance: singleViewData.isAllowance,
          allowanceName: singleViewData.allowanceName,
          _id: singleViewData._id,
        };
        setFormData(formattedFormData);
      }
    }, [singleViewData, payHead, employeeList, singleViewId]);

    // fetch employee based amount
    // useEffect(() => {
    //   if (formData.employee && formData.payHeadAllowance) {
    //     // console.log("--allowance", formData.payHeadAllowance);
    //     VandRAmtForEmployee({
    //       empId: formData?.employee._id,
    //       payHeadId: formData.payHeadAllowance._id,
    //       successCallback: (data) => {
    //         console.log(data);
    //         setFormData((prev) => ({ ...prev, amount: data?.monthly }));
    //       },
    //       errorCallback: (error) => {
    //         console.log(error);
    //         setFormData((prev) => ({ ...prev, amount: 0 }));
    //       },
    //     });
    //   } else {
    //     setFormData((prev) => ({ ...prev, amount: 0 }));
    //   }
    // }, [formData.employee, formData.payHeadAllowance]);
useEffect(() => {
  // ✅ Only fetch if NOT in edit mode, AND amount is not already set
  if (
    isEdit || 
    !formData.employee || 
    !formData.payHeadAllowance ||
    formData.amount !== 0 // Already has a value; don't overwrite
  ) return;

  VandRAmtForEmployee({
    empId: formData?.employee._id,
    payHeadId: formData.payHeadAllowance._id,
    successCallback: (data) => {
      setFormData((prev) => ({
        ...prev,
        amount: data?.monthly ?? 0,
      }));
    },
    errorCallback: (error) => {
      setFormData((prev) => ({ ...prev, amount: 0 }));
    },
  });
}, [formData.employee, formData.payHeadAllowance, isEdit]);

    return (
      <>
        {loading ? (
          <SkeletonForm section={section} />
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Autocomplete
                size="small"
                disabled={isEdit}
                options={payHead}
                value={formData.payHeadAllowance}
                onChange={handleChange("payHeadAllowance")}
                getOptionLabel={(option) => option?.name || ""}
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return false;
                  return option._id === value._id;
                }}
                renderOption={(props, option) => (
                  <li {...props} key={option._id}>
                    {option.name || "Unknown"}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Pay Head Allowance"
                    error={!!errors.payHeadAllowance}
                    helperText={errors.payHeadAllowance}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Autocomplete
                size="small"
                disabled={isEdit}
                options={employeeList}
                value={formData.employee}
                onChange={handleChange("employee")}
                getOptionLabel={(option) => option?.staff_name || ""}
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return false;
                  return option._id === value._id;
                }}
                renderOption={(props, option) => (
                  <li {...props} key={option._id}>
                    {option.staff_name || "Unknown"}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Employee"
                    error={!!errors.employee}
                    helperText={errors.employee}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>

            {section === "Variable" && (
              <Grid item xs={12} md={4}>
                <TextField
                  disabled={isEdit}
                  size="small"
                  type="date"
                  label="Date"
                  value={formData.from}
                  onChange={handleChange("from")}
                  error={!!errors.from}
                  helperText={errors.from}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}

            {section === "Recurring" && (
              <>
                <Grid item xs={12} md={4}>
                  <TextField
                    disabled={isEdit}
                    size="small"
                    type="date"
                    label="From"
                    value={formData.from}
                    onChange={handleChange("from")}
                    error={!!errors.from}
                    helperText={errors.from}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    disabled={isEdit}
                    size="small"
                    type="date"
                    label="To"
                    value={formData.to}
                    onChange={handleChange("to")}
                    error={!!errors.to}
                    helperText={errors.to}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    disabled={true}
                    type="number"
                    size="small"
                    label="Count"
                    value={formData.count}
                    onChange={handleChange("count")}
                    error={!!errors.count}
                    helperText={errors.count}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} md={4}>
              <TextField
                disabled={isEdit}
                type="number"
                size="small"
                label="Amount"
                value={formData.amount}
                onChange={handleChange("amount")}
                error={!!errors.amount}
                helperText={errors.amount}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        )}
      </>
    );
  }
);

export default VariableAndRecurringLayout;

const SkeletonForm = ({ section }) => {
  return (
    <Grid container spacing={3}>
      {/* Payhead Allowance */}
      <Grid item xs={12} md={4}>
        <Skeleton variant="rectangular" height={56} />
      </Grid>

      {/* Employee */}
      <Grid item xs={12} md={4}>
        <Skeleton variant="rectangular" height={56} />
      </Grid>

      {/* Date */}
      {section === "Recurring" && (
        <Grid item xs={12} md={4}>
          <Skeleton variant="rectangular" height={56} />
        </Grid>
      )}

      {/* From */}
      {section === "Variable" && (
        <Grid item xs={12} md={4}>
          <Skeleton variant="rectangular" height={56} />
        </Grid>
      )}

      {/* To */}
      {section === "Variable" && (
        <Grid item xs={12} md={4}>
          <Skeleton variant="rectangular" height={56} />
        </Grid>
      )}

      {/* Count */}
      {section === "Variable" && (
        <Grid item xs={12} md={4}>
          <Skeleton variant="rectangular" height={56} />
        </Grid>
      )}

      {/* Amount */}
      <Grid item xs={12} md={4}>
        <Skeleton variant="rectangular" height={56} />
      </Grid>
    </Grid>
  );
};
